import React, { useCallback, useRef, useState } from "react";
import { useCurrentProjectId } from "@adsk/alloy-provider";
import { GeoLocationPicker } from "@adsk/acc-locations-components";
import { Issue } from "@adsk/acc-issues-types";
import PagePopover from "../../utils/components/PagePopover";
import { useLocationsMapUrl } from "../../hooks/useLocationsMapUrl";
import { GpsCoordinates } from "@adsk/acc-issues-types";
import {
  trackGeoAction,
  GEO_EVENTS,
  GEO_ACTIONS,
  GEO_SET_METHODS,
  GEO_OPEN_MAP_SOURCES,
} from "../../utils/analytics/geoActionTracking";
import { isObject, has } from "lodash";
import { useIssuesRemoteOverrideUrl } from "../../hooks/useIssuesRemoteOverrideUrl";

export interface LocationPickerFieldProps {
  issue?: Issue;
  onChange: (changes?: GpsCoordinates) => void;
  error?: boolean;
  htmlId?: string;
  disabled?: boolean;
}

export const GeoLocationPickerField = ({
  issue,
  onChange,
  error = false,
  htmlId = "",
  disabled = false,
}: LocationPickerFieldProps) => {
  const [geoValue, setGeoValue] = useState(issue?.gpsCoordinates || null);
  const [isIframeModalOpen, setIsIframeModalOpen] = useState(false);
  const projectId = useCurrentProjectId() as string;
  const isMapDisabled =
    disabled || window.location.pathname.includes("/locations");
  const displayMapUrl = useLocationsMapUrl({ issueId: issue ? issue.id : "" });
  const inputMapUrl = useLocationsMapUrl(
    issue ? { issueId: issue.id, mode: "add" } : { mode: "place" }
  );
  //Because we have an iframe with the same data in the creation form,
  //we want to make sure that only the relevant component will use the data from the event,
  //otherwise we can experience a bug in which the create form is effecting the field in the edit form.
  const isLocationPlacementMode = useRef(false);

  const handleMessage = useCallback(
    event => {
      if (isObject(event.data) && has(event.data, "gpsCoordinates")) {
        setIsIframeModalOpen(false);
        if (isLocationPlacementMode.current && event.data.gpsCoordinates) {
          onChange(event.data.gpsCoordinates);
          setGeoValue(event.data.gpsCoordinates);
          if (issue) {
            //Track the event only in inline mode aka when there is an issue
            trackGeoAction(GEO_EVENTS.COORDINATES, {
              action: GEO_ACTIONS.SET,
              method: GEO_SET_METHODS.PLACED_WITH_MAP,
            });
          }
        }
        isLocationPlacementMode.current = false;
      }
    },
    [onChange, issue]
  );

  const getRemoteUrl = useIssuesRemoteOverrideUrl();

  const handleMapOpen = useCallback(() => {
    if (issue) {
      //Track the event only in inline mode aka when there is an issue
      trackGeoAction(GEO_EVENTS.OPEN_MAP_VIEW, {
        origin: GEO_OPEN_MAP_SOURCES.DETAILS,
      });
    }
    if (issue && issue.gpsCoordinates) {
      const updatedDisplayMapUrl = getRemoteUrl(displayMapUrl);
      window.open(updatedDisplayMapUrl, "_blank", "noopener");
    } else {
      isLocationPlacementMode.current = true;
      setIsIframeModalOpen(true);
    }
  }, [getRemoteUrl, displayMapUrl, issue]);

  const handleChange = useCallback(
    value => {
      //Convert null to undefined in issue creation so we won’t send redundant data
      if (!issue && !value) {
        onChange();
        return;
      }
      if (issue) {
        //Track the event only in inline mode aka when there is an issue
        trackGeoAction(GEO_EVENTS.COORDINATES, {
          action: !value
            ? GEO_ACTIONS.REMOVE
            : issue.gpsCoordinates
            ? GEO_ACTIONS.EDIT
            : GEO_ACTIONS.SET,
          method: GEO_SET_METHODS.TEXT_INPUT,
        });
      }
      onChange(value);
    },
    [onChange, issue]
  );

  return (
    <div data-testid="GeoLocationPickerField">
      <div style={!!issue ? { paddingTop: "20px" } : {}}>
        <GeoLocationPicker
          key={JSON.stringify(geoValue)}
          projectId={projectId}
          defaultValue={geoValue}
          onChange={handleChange}
          onOpenMap={handleMapOpen}
          disableOpenMap={isMapDisabled}
          disabled={disabled}
          isInlineEdit={!!issue}
          error={error}
          htmlId={htmlId}
        />
      </div>
      <PagePopover
        src={inputMapUrl}
        open={isIframeModalOpen}
        onMessage={handleMessage}
      />
    </div>
  );
};
